/**
 * The person's colour — one stored value per person, one reader for every
 * surface (signup A′; CD Drop 7, SYSTEM §8 "the person's colour, rebuilt").
 *
 * Colour is CHOSEN, never read from an image and never assigned per viewer:
 * everyone starts purple (`--colour-own`), each person picks one of seven on
 * their Profile, and the choice is stored on their profile (`profile_data
 * .colour`) — the same on every phone, delivered like a name (outbound in
 * buildProfilePayload, refreshed by get_connection_profiles, migration 034).
 *
 * --field-none (grey) is RESERVED for a record with no contact: never stored,
 * never offered in a picker. It's a render decision made from live content
 * (does the record have any way to reach them?).
 *
 * Pure — no DOM, no network. The setter is userDataStorage.saveOwnColour.
 */

/**
 * The seven a person can choose (Drop 7, SYSTEM §8 — "the same list for
 * everyone, in order"), as stored keys → their tokens. The stored value is
 * permanent data, so it is a NAME, never a token number CD might renumber.
 * Stored at `profile_data.colour`; absent = not chosen = purple.
 *
 * `stops` are each token's first and last gradient stop (163°: the top edge and
 * the foot) — for what a host paints outside the page (Safari's status strip,
 * the runway under a card). `ramp` is the whole gradient — its angle and every
 * stop — for computing the colour at a point on a card (the band under
 * Safari's bar, signup E3a). Both pinned to signup-tokens.css by test.
 */
const ramp = (mid, midAt, stops) => ({
  angle: 163,
  stops: [
    [stops[0], 0],
    [mid, midAt],
    [stops[1], 100],
  ],
});
export const PERSON_COLOURS = [
  {
    key: 'purple',
    token: 'var(--colour-own)',
    stops: ['#2E3A8C', '#B8478A'],
    ramp: ramp('#6B3FA0', 46, ['#2E3A8C', '#B8478A']),
  },
  {
    key: 'blue',
    token: 'var(--colour-1)',
    stops: ['#00456f', '#4b95e5'],
    ramp: ramp('#006aaf', 50, ['#00456f', '#4b95e5']),
  },
  {
    key: 'lagoon',
    token: 'var(--colour-10)',
    stops: ['#004d56', '#00a4cf'],
    ramp: ramp('#007792', 50, ['#004d56', '#00a4cf']),
  },
  {
    key: 'green',
    token: 'var(--colour-3)',
    stops: ['#004c09', '#00ad80'],
    ramp: ramp('#007a3a', 50, ['#004c09', '#00ad80']),
  },
  {
    key: 'amber',
    token: 'var(--colour-5)',
    stops: ['#8c3300', '#e19b1b'],
    ramp: ramp('#bc5e00', 50, ['#8c3300', '#e19b1b']),
  },
  {
    key: 'coral',
    token: 'var(--colour-2)',
    stops: ['#94212a', '#f9875e'],
    ramp: ramp('#cb4838', 50, ['#94212a', '#f9875e']),
  },
  {
    key: 'raspberry',
    token: 'var(--colour-12)',
    stops: ['#8b225b', '#f97f95'],
    ramp: ramp('#c54476', 50, ['#8b225b', '#f97f95']),
  },
];

/** No way to reach them — the reserved grey (never one of the seven). */
export const NO_CONTACT = {
  token: 'var(--field-none)',
  stops: ['#3C4048', '#868C94'],
  ramp: ramp('#5A6068', 52, ['#3C4048', '#868C94']),
};

const BY_KEY = Object.fromEntries(PERSON_COLOURS.map((c) => [c.key, c]));
const DEFAULT = BY_KEY.purple;

/** Not chosen yet — everyone starts here (SYSTEM §8 rule 1). */
export const DEFAULT_COLOUR_VAR = DEFAULT.token;

/** No way to reach them — the reserved grey. */
export const NO_CONTACT_VAR = NO_CONTACT.token;

/** A storable chosen colour: one of the seven keys. */
export const isPersonColour = (c) =>
  typeof c === 'string' && Object.prototype.hasOwnProperty.call(BY_KEY, c);

// The one decision every reader shares: no contact → grey; a chosen key → it;
// anything else (not chosen, or unknown to this build) → purple.
const colourOf = (person, { hasContact = true } = {}) => {
  if (!hasContact) return NO_CONTACT;
  return isPersonColour(person?.colour) ? BY_KEY[person.colour] : DEFAULT;
};

/**
 * The person's colour, as a CSS background — what every surface paints behind
 * a round face, under a logo, or as a photo-less field. Nothing recomputes it.
 *
 * No contact → the reserved grey. Otherwise their chosen colour; not chosen
 * (or a value this build doesn't know, e.g. from a newer app) → purple, the
 * spec's start colour for everyone.
 *
 * @param {{colour?: string|null}} person - a record or profile carrying `colour`
 * @param {{hasContact?: boolean}} [opts]
 * @returns {string} a var() reference into signup-tokens.css
 */
export const personColourVar = (person, opts) => colourOf(person, opts).token;

/**
 * The person's colour as its [top, foot] stops — the same decision as
 * personColourVar, for the strips a page can't paint with a gradient.
 *
 * @param {{colour?: string|null}} person
 * @param {{hasContact?: boolean}} [opts]
 * @returns {[string, string]} hex colours
 */
export const personColourStops = (person, opts) => colourOf(person, opts).stops;

/**
 * The person's colour as its whole gradient ({ angle, stops: [[hex, %], …] }) —
 * the same decision as personColourVar.
 *
 * @param {{colour?: string|null}} person
 * @param {{hasContact?: boolean}} [opts]
 */
export const personColourRamp = (person, opts) => colourOf(person, opts).ramp;

const hexToRgb = (hex) => {
  const n = parseInt(hex.slice(1), 16);
  return [(n >> 16) & 255, (n >> 8) & 255, n & 255];
};
const toHex = (rgb) => `#${rgb.map((v) => Math.round(v).toString(16).padStart(2, '0')).join('')}`;

/**
 * The colour a CSS linear-gradient paints at one point of a box — computed from
 * the gradient's own angle and stops and the box's geometry, never eyeballed
 * (the CSS gradient-line rule: length |W·sin a| + |H·cos a| through the centre;
 * sRGB interpolation between stops).
 *
 * @param {{angle: number, stops: Array<[string, number]>}} ramp
 * @param {{width: number, height: number, x: number, y: number}} at
 * @returns {string} #rrggbb
 */
export const rampColourAt = (ramp, { width, height, x, y }) => {
  const a = (ramp.angle * Math.PI) / 180;
  const len = Math.abs(width * Math.sin(a)) + Math.abs(height * Math.cos(a));
  const along = (x - width / 2) * Math.sin(a) + (y - height / 2) * -Math.cos(a);
  const t = Math.min(100, Math.max(0, (0.5 + along / len) * 100));
  const { stops } = ramp;
  let i = 1;
  while (i < stops.length - 1 && t > stops[i][1]) i += 1;
  const [c0, p0] = stops[i - 1];
  const [c1, p1] = stops[i];
  const f = p1 === p0 ? 0 : (t - p0) / (p1 - p0);
  const a0 = hexToRgb(c0);
  const a1 = hexToRgb(c1);
  return toHex(a0.map((v, k) => v + (a1[k] - v) * f));
};
